/**
 * Streaming (SSE) chat transport: the `ChatboxStreamFactory` backed by the SHARED
 * `ChatTransport` (`ai-teammate/utils/chat-transport.js`) hitting the real pi-server SSE endpoints
 * (`POST .../threads/stream`, `.../threads/:threadId/stream`, and resume via active-stream). Same
 * factory shape as the buffered path, so the controller swaps transports behind the adapter with no
 * surface change; `request.signal` threads through to the fetch so Stop aborts the in-flight turn.
 */

import type { AiSdkUIMessageChunk } from "@mui/x-chat"
import type { ChatDraftAttachment, ChatMessage } from "@mui/x-chat-headless"
import { assembleAssistantMessage } from "./adapter"
import type { ChatboxStreamFactory, ChatboxUIMessageStream, ProgressFlushScheduler } from "./adapter"
import { log } from "helpers/logger"
import { chatApiErrorKindForServerType, describeChatApiError, describeInterruptedStream, isExpectedStreamGoneError } from "./chat-api-errors"
import type { ChatApiErrorKind } from "./chat-api-errors"
import { type FailedTurnAssistantIdSink, type FinishMetadataSink, errorChunk, errorKindOf, errorTextOf, errorTypeOf, messageText, normalizeChunkStream } from "./normalize-chunks"

/** The subset of the shared `ChatTransport` the chatbox surface drives (the JS class is untyped). */
export interface StreamingChatTransport {
  sendMessages(args: { chatId: string | null, body: unknown, abortSignal: AbortSignal }): ChatboxUIMessageStream
  reconnectToStream(args: { chatId: string, abortSignal: AbortSignal, tolerateNonTerminalEnd?: boolean, fromStart?: boolean }): Promise<ChatboxUIMessageStream | null>
  /**
   * Server-side stop: DELETE the (chatId ?? active) stream so op-summarize halts generation (APP-3394).
   * Optional — the current shared transport has no such method; it arrives with the drawer's stop (#4141),
   * so callers optional-chain it and the chatbox no-ops until then.
   */
  cancelStream?(chatId: string | null): Promise<void>
}

const NO_DRAFT_ATTACHMENTS: readonly ChatDraftAttachment[] = []

/** Opens the SSE turn for the current send context: start (no chatId) or continue (chatId), aborting on `signal`. */
export type OpenStreamingTurn = (
  userText: string,
  drafts: readonly ChatDraftAttachment[],
  signal: AbortSignal,
) => Promise<ChatboxUIMessageStream>

/** Build the streaming `ChatboxStreamFactory`; the caller closes over the send context (thread id, body), mirroring the buffered factory. */
export function createStreamingChatStream({ openStreamingTurn }: { openStreamingTurn: OpenStreamingTurn }): ChatboxStreamFactory {
  return (request) => openStreamingTurn(messageText(request.message), request.attachments ?? NO_DRAFT_ATTACHMENTS, request.signal)
}

/**
 * How a turn ended, for the caller to report. Only turns that reached a conclusion are described: an ABORTED
 * read is either the composer's Stop — which reports itself, at the button, where the intent is unambiguous —
 * or a nav-away, which is not an outcome of anything.
 */
export interface TurnOutcome {
  status: "completed" | "failed"
  /** Wall time from the turn's registration to its settle. */
  durationMs: number
  /** Present on `failed`, for the caller to classify (describeChatApiError). */
  error?: unknown
  /**
   * This app's taxonomy bucket, when the failure named one. Preferred over classifying `error`: a server-side
   * failure reaches us as a terminal error CHUNK, and the only thing reconstructable from that is its rendered
   * copy — which classifies as "unknown" and would make the whole reliability taxonomy one bucket.
   *
   * Always this app's vocabulary, whichever transport carried the turn: the buffered POST classifies its HTTP
   * error, and a server exception class is mapped through `chatApiErrorKindForServerType`. Reporting the raw
   * class here instead would have split the taxonomy by TRANSPORT — and put `operation: "send"` on a different
   * vocabulary from the `history` and `thread_list` failures it is meant to be compared with.
   */
  kind?: ChatApiErrorKind
  /** The server's own exception class, where it named one — the detail the shared taxonomy deliberately loses. */
  errorType?: string
}

/** The active-turn registration the controller supplies so a factory turn (composer OR programmatic) is observable + stoppable regardless of initiator. */
export interface ActiveTurnHooks {
  /** Register a fresh controller as the active turn; its `signal` is what the transport aborts on. */
  beginTurn: () => AbortController
  /** Clear the active turn once its stream settles (only if still the registered one). */
  endTurn: (controller: AbortController) => void
  /** Reported once per concluded turn. Here rather than at the call sites because this wrapper is the only
   *  thing every turn passes through, whoever started it. */
  onTurnOutcome?: (outcome: TurnOutcome) => void
}

function calmErrorStream(signal: AbortSignal, error: unknown): ChatboxUIMessageStream {
  return new ReadableStream<AiSdkUIMessageChunk>({
    start(controller) {
      // A signal-abort is expected teardown, not an error to render.
      if (!signal.aborted) {
        if (!isExpectedStreamGoneError(error)) log(error)
        controller.enqueue(errorChunk(describeChatApiError(error).message))
      }
      controller.close()
    },
  })
}

/** What an error chunk tells us about the failure: this app's bucket, plus the server's class where it named one. */
type TurnFailureSignal = Pick<TurnOutcome, "kind" | "errorType">

/**
 * Normalize a terminal error chunk to ONE vocabulary. The buffered POST classified its HTTP error already; the
 * SSE mapping can only name the server's exception class, which is mapped here so both transports report the
 * same `kind` for the same failure. The raw class rides along untouched.
 */
function failureSignalOf(chunk: AiSdkUIMessageChunk): TurnFailureSignal {
  const errorType = errorTypeOf(chunk)
  const kind = (errorKindOf(chunk) as ChatApiErrorKind | null) ?? chatApiErrorKindForServerType(errorType)
  return { ...(kind != null && { kind }), ...(errorType != null && { errorType }) }
}

/**
 * Pass the source through, settling the turn on close/error/cancel; a transport-level rejection becomes one calm
 * errorChunk (a signal-abort stays silent). `settle` is handed the error the turn died of, or nothing if it
 * reached its answer — a failed turn is NOT a completed one, and only this wrapper can tell them apart.
 */
function guardedStream(source: ChatboxUIMessageStream, signal: AbortSignal, settle: (error?: unknown, failure?: TurnFailureSignal) => void): ChatboxUIMessageStream {
  const reader = source.getReader()
  let settled = false
  // Both transports report a server-side failure as a terminal `error` CHUNK and then close cleanly
  // (chat-transport.js maps the SSE `error` event; the buffered POST enqueues one) — so the chunk, not the
  // close, is what decides the outcome. Only a network drop reaches the catch below.
  let streamError: unknown
  let streamFailure: TurnFailureSignal | undefined
  // A failed CONNECT arrives in that catch too, not in calmErrorStream: sendMessages returns its stream before
  // the fetch runs, so only a synchronous throw in the factory rejects `await factory(...)`. Only a turn that
  // already produced output can be called a dropped connection; before that, nothing was in flight to drop.
  let sawChunk = false
  const done = (error?: unknown) => {
    if (settled) return
    settled = true
    settle(error ?? streamError, streamFailure)
  }
  return new ReadableStream<AiSdkUIMessageChunk>({
    async pull(controller) {
      try {
        const { value, done: finished } = await reader.read()
        if (finished) {
          controller.close()
          done()
          return
        }
        // Aborted (Stop or nav-away) while this read was pending: drop the buffered chunk instead of emitting
        // it — a finish sitting in the fetch buffer must not reach the finish tap and adopt/hijack the new
        // view. Checked after the read (like the buffered transport) so both paths settle identically.
        if (signal.aborted) {
          controller.close()
          done()
          return
        }
        if (value.type === "error") {
          streamError = new Error(errorTextOf(value))
          streamFailure = failureSignalOf(value)
        }
        controller.enqueue(value)
        sawChunk = true
      } catch (error) {
        done(error)
        if (signal.aborted) {
          controller.close()
          return
        }
        // The chunk carries copy only, so this is the last holder of the status, the body code and the stack.
        if (!isExpectedStreamGoneError(error)) log(error)
        controller.enqueue(errorChunk(describeInterruptedStream(error, { streamed: sawChunk })))
        controller.close()
      }
    },
    cancel(reason) {
      done()
      return reader.cancel(reason)
    },
  })
}

/**
 * Wrap any factory so each turn (a) registers a controller in the controller-held ref — linked to the
 * caller's request signal so composer Stop still aborts cleanly — making the turn observable/stoppable
 * regardless of initiator, and (b) surfaces transport-level failures as a single calm errorChunk to match
 * the buffered path (the streaming transport otherwise rejects into MUI's generic error).
 */
export function withActiveTurn(factory: ChatboxStreamFactory, { beginTurn, endTurn, onTurnOutcome }: ActiveTurnHooks): ChatboxStreamFactory {
  return async (request) => {
    const startedAt = Date.now()
    const controller = beginTurn()
    if (request.signal.aborted) controller.abort()
    else request.signal.addEventListener("abort", () => controller.abort(), { once: true })
    const report = (status: TurnOutcome["status"], error?: unknown, failure?: TurnFailureSignal) => {
      // See TurnOutcome: an aborted turn is a Stop or a nav-away, and neither is this wrapper's to report.
      if (controller.signal.aborted) return
      onTurnOutcome?.({ status, durationMs: Date.now() - startedAt, error, ...failure })
    }
    const settle = (error?: unknown, failure?: TurnFailureSignal) => {
      endTurn(controller)
      report(error === undefined ? "completed" : "failed", error, failure)
    }
    let source: ChatboxUIMessageStream
    try {
      source = await factory({ ...request, signal: controller.signal })
    } catch (error) {
      endTurn(controller)
      report("failed", error)
      return calmErrorStream(controller.signal, error)
    }
    return guardedStream(source, controller.signal, settle)
  }
}

/** Taps for a resume reattach: finish-metadata (turn ids) and per-frame progress (live-token render, APP-3376). */
export interface ReattachStreamOptions {
  onFinishMetadata?: FinishMetadataSink
  /** The assistant row a failed turn persisted: a turn can fail while the client is reattaching, and the
   * continue guard has to move for that path too, exactly as it does on the live-send path. */
  onFailedTurnAssistantId?: FailedTurnAssistantIdSink
  /** Coalesced partial snapshots as the replay arrives — wire it to render the resumed turn token-by-token. */
  onProgress?: (partial: ChatMessage) => void
  /** Coalescing scheduler forwarded to the assembler; defaults to requestAnimationFrame. Injectable for tests. */
  scheduleProgressFlush?: ProgressFlushScheduler
  /** The server is reconnecting to a stalled model provider, on a REATTACHED turn. Same tap as the live-send path. */
  onStreamRetryingChange?: (retrying: boolean) => void
}

/**
 * Attach-on-select resume: reconnect to a thread's in-flight stream (`active-stream` + from-start replay) and assemble the reattached turn. Returns `null` when the thread has no active
 * stream. The finish tap captures the turn ids so the assembled turn stays consistent with the live-send path;
 * `onProgress` emits coalesced partials so the reattached turn renders token-by-token (APP-3376).
 * `tolerateNonTerminalEnd`: a replay that drops before its terminal event still yields what it assembled — a
 * partial answer under the fallback id (no synthetic finish; the drop closes the open text part in the transport).
 * `fromStart`: replay the WHOLE active stream, not from the tail cursor — Kelly's controlled view is empty on
 * attach (it wipes live turns on nav-away), so resuming from the tail would drop every interstitial narrative and
 * tool call buffered before the attach (APP-3376).
 */
export async function reattachActiveStream(
  transport: StreamingChatTransport,
  chatId: string,
  signal: AbortSignal,
  fallbackMessageId: string,
  options?: ReattachStreamOptions,
): Promise<ChatMessage | null> {
  const source = await transport.reconnectToStream({ chatId, abortSignal: signal, tolerateNonTerminalEnd: true, fromStart: true })
  if (source == null) return null
  const { onFinishMetadata, onFailedTurnAssistantId, onProgress, scheduleProgressFlush, onStreamRetryingChange } = options ?? {}
  return assembleAssistantMessage(normalizeChunkStream(source, { onFinishMetadata, onFailedTurnAssistantId, onStreamRetryingChange }), fallbackMessageId, { onProgress, scheduleProgressFlush })
}
